import { OrbitsGame, getGridCenter } from './OrbitsGame.js';
import { OrbitsRenderer } from './OrbitsRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Edge } from '../../core/GridHitDetector.js';
import { Direction, Position, Outside, pointsEqual, isInside } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, FinePoint, FineTraceStep } from '../../input/KeyboardController.js';
import { fineToEdge, fineToCell } from '../../input/fineGeometry.js';

export class OrbitsInput extends BaseInput<OrbitsGame, OrbitsRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: OrbitsGame,
        renderer: OrbitsRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const orbitEdge = this.renderer.getOrbitEdgeFromPixel(x, y);
        if (orbitEdge && this.game.hasGate(orbitEdge)) {
            this.game.toggleGate(orbitEdge);
            this.onUpdate();
        }
    };

    /**
     * Check if edge is one of the 4 edges connected to center intersection.
     * Returns the launcher direction (N/E/S/W) or null if not a center edge.
     */
    private getCenterEdgeDirection(edge: Edge): Direction | null {
        const center = getGridCenter(this.game.getGrid().size);
        const touchesCenter = pointsEqual(edge.pa, center) || pointsEqual(edge.pb, center);
        if (!touchesCenter) return null;

        // Find the other point (not the center)
        const other = pointsEqual(edge.pa, center) ? edge.pb : edge.pa;

        if (edge.orient === 'v') {
            // Vertical edge: N if other point is above center, S if below
            return other.row < center.row ? 'N' : 'S';
        } else {
            // Horizontal edge: W if other point is left of center, E if right
            return other.col < center.col ? 'W' : 'E';
        }
    }

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        // Gate mode: vortex gate activation/deactivation
        if (this.gateMode && isVortexGame(this.game)) {
            const cell = this.detector.detectCell(x, y);
            if (cell && this.game.toggleVortexGate(cell)) {
                this.onUpdate();
            }
            return;
        }

        // Click at the very center flips the orbit direction (clockwise/CCW)
        const center = getGridCenter(this.game.getGrid().size);
        const intersection = this.detector.detectIntersection(x, y, 0.38);
        if (intersection && pointsEqual(intersection, center)) {
            // Keep any selected digit: flipping the orbit is exactly the edit you
            // want to watch against the path you're inspecting.
            this.game.toggleClockwise();
            this.onUpdate();
            return;
        }

        // First check for center edge click (launcher placement)
        const edge = this.detector.detectEdge(x, y);
        if (edge) {
            const centerDirection = this.getCenterEdgeDirection(edge);
            if (centerDirection) {
                this.game.setLauncher(centerDirection);
                this.game.clearSelectedDigit();
                this.onUpdate();
                return;
            }
        }

        // Then check for orbit edge click (gate placement)
        const orbitEdge = this.renderer.getOrbitEdgeFromPixel(x, y);
        if (orbitEdge) {
            if (this.game.toggleGate(orbitEdge)) {
                this.onUpdate();
            }
            return;
        }

        // Check for digit click (toggle path display)
        const cell = this.detector.detectCell(x, y);
        if (cell) {
            const grid = this.game.getGrid();
            const digit = grid.getDigitAt(cell);
            if (digit !== null) {
                this.game.toggleSelectedDigit(digit);
                this.onUpdate();
            }
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Gates sit on edges, so the cursor rides the half-grid like Walls — extended to
    // the rim, since exit gates live on the boundary. A tap toggles the gate under
    // the cursor (or aims the launcher / flips the orbit at the very center); the
    // trace steers the ship along the path you walk.

    usesFineCursor(): boolean {
        return true;
    }

    /** Exit gates are on the grid boundary, so the cursor must reach the rim. */
    allowsFineRim(): boolean {
        return true;
    }

    /** Each hop steers the ship somewhere — the hint shows the heading it took. */
    wantsTraceHint(): boolean {
        return true;
    }

    /** The ship exits through a gate rather than reappearing on the far side, so
     *  the cursor stops at the boundary instead of wrapping. */
    wrapsFineCursor(): boolean {
        return false;
    }

    placeAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;

        // The very center: flip the orbit direction, as clicking the center icon does.
        const center = getGridCenter(this.game.getGrid().size);
        if (p.r === 2 * center.row && p.c === 2 * center.col) {
            this.game.toggleClockwise();
            return true;
        }

        // A cell center: tapping a digit toggles its path hint, as clicking it does.
        const cell = fineToCell(p);
        if (cell) {
            const digit = this.game.getGrid().getDigitAt(cell);
            if (digit === null) return false;
            this.game.toggleSelectedDigit(digit);
            return true;
        }

        const edge = this.fineToEdge(p);
        if (!edge) return false;

        // The four edges meeting the center aim the launcher.
        const launchDir = this.getCenterEdgeDirection(edge);
        if (launchDir) {
            this.game.setLauncher(launchDir);
            this.game.clearSelectedDigit();
            return true;
        }

        return this.game.toggleGate(edge);
    }

    deleteAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;
        const edge = this.fineToEdge(p);
        if (!edge || !this.game.hasGate(edge)) return false;
        return this.game.toggleGate(edge);
    }

    /**
     * A cell-to-cell trace hop: make the ship do what the player just did. Leaving
     * `cell` having arrived on `fromDir`, the gate that sends it off on `moveDir` is
     * toggled — see `OrbitsGame.steerShip`. On the opening hop the ship's heading
     * isn't known yet, but changing rings always needs a gate, so an empty slot on
     * the boundary being crossed is opened.
     */
    traceFine({ cell, fromDir, moveDir }: FineTraceStep): boolean {
        if (this.gateMode) return false;
        // On the opening hop the cell the trace began on counts too, so starting a
        // trace on a digit shows its path rather than waiting for the next one.
        if (fromDir === null) this.selectDigitAt(cell);
        this.selectDigitAt(this.game.getGrid().move(cell, moveDir));

        return fromDir === null
            ? this.game.openTrace(cell, moveDir)
            : this.game.steerShip(cell, fromDir, moveDir);
    }

    /**
     * Show a digit's onward path when the trace touches its cell, so the leg being
     * drawn stays visible as you draw it. The selection sticks until the next digit
     * is crossed — the hint follows the trace rather than blinking on and off.
     */
    private selectDigitAt(cell: Position | Outside): void {
        if (!isInside(cell)) return;
        const digit = this.game.getGrid().getDigitAt(cell);
        if (digit !== null) this.game.selectDigit(digit);
    }

    private fineToEdge(p: FinePoint): Edge | null {
        return fineToEdge(this.game.getGrid(), p);
    }

    /** `X` goes to the grid center — the launcher hub everything orbits around. */
    fineCursorOrigin(): FinePoint | null {
        const center = getGridCenter(this.game.getGrid().size);
        return { r: 2 * center.row, c: 2 * center.col };
    }
}
